import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../../../../../shared/empty-state/empty-state'
import { IconSearch } from '../../../../../../shared/icon/icon'
import { TYPE_ALL, TYPE_COURSES, TYPE_FOLDERS } from '../../../../../../shared/search/search-index'
import { kindFor } from '../../../../../../shared/search/kinds'
import { platformLabel } from '../platform/platforms'
import { PlatformIcons } from '../platform/platform-controls'
import { NodeUpdated } from '../shared/node-updated'
import { formatLabel } from '../format/formats'
import { ListError } from './list-error'
import { SearchHighlight } from './search-highlight'
import { SearchPath } from './search-path'
import { SearchThumbnail } from './search-thumbnail'
import '../../../../../../shared/sso-workspace/sso-workspace.scss'

// One result (owner 2026-10-09: "show thumbnails", "difficult to know where
// the results live"): its card's picture; its name, the row's link; where it
// lives (SearchPath, each folder a link of its own); and what its card shows
// -- its kind as a pill with the kind's icon, and for a course or template
// where it runs (the card's platform capsule, or "Not published") and when
// it was updated. The name's link stretches over the row, so a click (or a
// middle-click) anywhere opens the result; the location's links sit above
// it. Memoized: moving the active row re-renders two rows, not fifty.
const SearchRow = React.memo(({ area, nodes, result, index, id, active, href, asset, onHover }) => {
  const entry = result.entry
  const node = entry.node || {}
  const kind = kindFor(area.kinds, node.datatype)
  const Icon = area.icons[kind.label]
  const course = entry.kind === 'course' && !entry.isVideo
  const builds = area.platforms && course
  const published = builds && Array.isArray(node.platforms) && node.platforms.length > 0

  return (
    <div
      id={id}
      role="option"
      aria-selected={active ? 'true' : 'false'}
      className={active ? 'node-search-row active' : 'node-search-row'}
      onMouseMove={() => !active && onHover(index)}>
      <SearchThumbnail asset={asset} folder={entry.kind === 'folder'} />
      <div className="node-search-body">
        <Link className="node-search-name" to={href}>
          <SearchHighlight text={entry.displayName} indices={result.nameIndices} />
        </Link>
        <SearchPath area={area} nodes={nodes} entry={entry} indices={result.pathIndices} />
        <div className="sw-workspace node-search-meta">
          <span className="sw-pill node-search-kind">
            {Icon && <Icon />}
            {kind.label}
          </span>
          {published && <PlatformIcons platforms={node.platforms} />}
          {builds && !published && <span className="node-search-unpublished">Not published</span>}
          {builds && <NodeUpdated node={node} />}
        </div>
      </div>
    </div>
  )
})

const resultsWord = n => (n === 1 ? '1 result' : `${n} results`)

// The search's results in place of a node area's grid (spec 2026-10-09
// §5.1): type chips with counts, then a ranked list -- not a table: a
// relevance order must not offer column sorting -- whose rows are real
// links (middle-click and Cmd-click open a new tab). Each row: the kind's
// icon, the name with its matched letters marked, the kind ("Course",
// "Folder", "Video", ...), the breadcrumb (ancestors, " › ") and, for a
// course, where it runs. The first 50, then "Show all N". `thumbnailOf(id)`
// is the node's asset-thumbnail row, if it has one.
export const NodeSearchResults = ({ area, nodes, thumbnailOf, search, listId, hrefOf, query, loaded, error, onRetry, platform, format, onClear, onClearPlatform, onClearFormat }) => {
  const listRef = useRef(null)

  // Keep the active row in view as ↑/↓ move it.
  useEffect(
    () => {
      const row = listRef.current && listRef.current.querySelector('[aria-selected="true"]')
      if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' })
    },
    [search.active]
  )

  if (error) {
    const where = platform ? ` that run on ${platformLabel(platform)}` : ''
    return <ListError message={`Couldn't search the ${area.noun}${where}.`} error={error} onRetry={onRetry} />
  }

  if (!loaded) {
    return (
      <div className="node-search-results">
        <p className="node-search-loading" role="status">
          Loading…
        </p>
      </div>
    )
  }

  // The first keystroke is scored 50 ms later: nothing to show until then.
  if (!search.searching) return null

  const { counts, results, total, type } = search
  const chips = [
    { type: TYPE_ALL, label: 'All', count: counts.all },
    { type: TYPE_COURSES, label: area.chips.courses, count: counts.courses },
    { type: TYPE_FOLDERS, label: area.chips.folders, count: counts.folders }
  ]

  // While the next keystroke waits to be scored, what is on screen stays
  // (the viewer's rule): the rows, or the "no matches" for the last query.
  const narrowed = [platform && `runs on ${platformLabel(platform)}`, format && `is ${formatLabel(format)}`].filter(Boolean)
  const noMatch = results.length === 0
  const shownQuery = search.scoredQuery || query

  return (
    <div className="node-search-results">
      <div className="sw-workspace node-search-chips" role="group" aria-label="Show">
        {chips.map(chip =>
          <button
            key={chip.type}
            type="button"
            className="btn btn-sm sw-btn"
            aria-pressed={type === chip.type ? 'true' : 'false'}
            onClick={() => search.setType(chip.type)}>
            {chip.label}
            <span className="node-search-count">
              {chip.count}
            </span>
          </button>
        )}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {resultsWord(total)}
      </p>

      {noMatch
        ? <EmptyState
            Icon={IconSearch}
            title="No matches"
            message={narrowed.length ? `Nothing that ${narrowed.join(' and ')} matches “${shownQuery}”.` : `Nothing matches “${shownQuery}”.`}
            action={{ label: 'Clear search', onClick: onClear }}
            secondary={
              onClearPlatform
                ? { label: 'Show all platforms', onClick: onClearPlatform }
                : onClearFormat ? { label: 'Show every format', onClick: onClearFormat } : undefined
            }
          />
        : <div className="node-search-list" role="listbox" id={listId} aria-label="Search results" ref={listRef}>
            {results.map((result, i) =>
              <SearchRow
                key={result.entry.id}
                area={area}
                nodes={nodes}
                result={result}
                index={i}
                id={`${listId}-${i}`}
                active={i === search.active}
                href={hrefOf(result)}
                asset={thumbnailOf(result.entry.id)}
                onHover={search.setActive}
              />
            )}
          </div>}

      {!noMatch &&
        results.length < total &&
        <div className="sw-workspace node-search-more">
          <button type="button" className="btn btn-sm sw-btn" onClick={search.showAll}>
            Show all {total}
          </button>
        </div>}
    </div>
  )
}
